import React, { useState, useLayoutEffect, useEffect, useMemo } from "react";
import PropTypes from "prop-types";
import * as Sentry from "@sentry/react";
import Action from "../../components/Action";
import Card from "../../components/Card";
import Confirmation from "../../components/Confirmation";
import Image from "../../components/Image";
import imageOfficeWorking from "../../images/office-working.png";
import QuestionDescription from "../../components/QuestionDescription";
import ReviewTable from "../../components/ReviewTable";
import ReviewText from "../../components/ReviewText";
import RenderQuestion from "../ClientDashboard/utils/RenderQuestion";
import ReviewQuestion from "../ClientDashboard/utils/ReviewQuestion";
import ScrollToTop from "../../components/utils/ScrollToTop";
import useDynamicFormSubmission from "../../hooks/useDynamicFormSubmission";
import { sanitiseHtml } from "../../utils/sanitiseHtml";

/**
 * Anonymous Information Request Form Component
 * Uses the shared useDynamicFormSubmission hook for form logic.
 * Submits to /api/anonymous_doable/{doableId}
 */
const AnonymousRequestForm = ({ doable: rawDoable, pageWidth }) => {
  // Anonymous information requests do not support uploads, and the staff app
  // rejects a document item in an anonymous request. Drop one that arrives
  // anyway instead of rendering an uploader that can never satisfy this form,
  // and report it: the client will submit without ever being asked for it.
  const { doable, droppedDocumentIdentifiers } = useMemo(() => {
    const dropped = rawDoable.configuration.fields
      .filter((field) => field.field_type === "document_upload")
      .map((field) => field.field_identifier);
    return {
      droppedDocumentIdentifiers: dropped,
      doable: {
        ...rawDoable,
        configuration: {
          ...rawDoable.configuration,
          fields: rawDoable.configuration.fields.filter((field) => field.field_type !== "document_upload"),
        },
      },
    };
  }, [rawDoable]);

  useEffect(() => {
    if (droppedDocumentIdentifiers.length > 0) {
      Sentry.captureMessage("Anonymous information request dropped document entries", {
        level: "warning",
        tags: { action: "AnonymousRequestForm" },
        extra: { doableId: rawDoable.id, identifiers: droppedDocumentIdentifiers },
      });
    }
  }, [rawDoable.id, droppedDocumentIdentifiers]);

  const {
    responses,
    errors,
    errorMessages,
    reviewing,
    setReviewing,
    handleInputChange,
    handleCheckboxChange,
    handleDateChange,
    handleFieldValidation,
    handleDateFieldValidation,
    handleValidation,
    reviewResponses,
    showPrevious,
    buildSubmission,
    fieldRefs,
    fieldToFocus,
    focusField,
  } = useDynamicFormSubmission(doable.configuration);

  const [submitted, setSubmitted] = useState(false);
  const [submissionError, setSubmissionError] = useState("");
  const [isDuplicateSubmission, setIsDuplicateSubmission] = useState(false);

  // Focus field when returning from review mode
  useLayoutEffect(() => {
    if (!reviewing && fieldToFocus) {
      focusField(fieldToFocus);
    }
  }, [reviewing, fieldToFocus, focusField]);

  // Scroll to top when entering error, success, or review states
  useEffect(() => {
    if (submissionError || submitted || reviewing) {
      window.scrollTo(0, 0);
    }
  }, [submissionError, submitted, reviewing]);

  const submitResponses = async () => {
    const { fieldsSubmission, fieldsToUnset } = buildSubmission();

    const submissionBody = {
      fields: fieldsSubmission,
    };

    if (fieldsToUnset.length > 0) {
      submissionBody.fields_to_unset = fieldsToUnset;
    }

    try {
      const resp = await fetch(`/api/anonymous_doable/${doable.id}`, {
        method: "PUT",
        credentials: "same-origin",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify(submissionBody),
      });
      if (!!resp.ok) {
        setSubmitted(true);
        setReviewing(false);
      } else {
        if (resp.status === 409) {
          setSubmissionError("Form already submitted");
          setIsDuplicateSubmission(true);
        } else {
          setSubmissionError("Unable to submit form");
        }
        setSubmitted(false);
        setReviewing(false);
      }
    } catch (err) {
      setSubmissionError("Unable to submit form");
      setIsDuplicateSubmission(false);
      setSubmitted(false);
      setReviewing(false);
    }
  };

  const saveResponses = () => {
    if (handleValidation()) {
      submitResponses();
    }
  };

  const createContent = (content) => {
    return { __html: sanitiseHtml(content) };
  };

  if (!doable) return null;

  const requestTitle = doable.title;
  const requestDescription = doable.description;
  const checkBeforeSubmit = doable.configuration.check_before_submit;

  if (!!submissionError) {
    return (
      <>
        <Card title="Sorry, that didn’t work" removeTopPadding>
          {isDuplicateSubmission ? (
            <p>You’ve already submitted this form.</p>
          ) : (
            <>
              <p>We’re having trouble submitting your answers at the moment.</p>
              <p>
                Please send an email to <a href="mailto:hello@juno.legal">hello@juno.legal</a> and let us know you were
                filling in the form:
              </p>
              <p>
                <em>{requestTitle}</em>
              </p>
            </>
          )}
        </Card>
      </>
    );
  }

  if (submitted) {
    return (
      <>
        <Confirmation title="Thanks! We’ve got your responses" pageWidth={pageWidth}>
          <p>We’ll review your information and get back to you if we need anything else.</p>
        </Confirmation>
        <Image src={imageOfficeWorking} fullWidth={true} />
      </>
    );
  }

  if (reviewing) {
    return (
      <>
        <Card title={requestTitle} removeTopPadding>
          {requestDescription && <QuestionDescription dangerouslySetInnerHTML={createContent(requestDescription)} />}
          <ReviewText>
            We’ve listed your answers below — if anything doesn’t look right, you can change your answers before you
            submit.
          </ReviewText>
          <ReviewTable>
            <thead>
              <tr>
                <th>We asked</th>
                <th>Your answer</th>
              </tr>
            </thead>
            <tbody>
              {doable.configuration.fields.map((fieldConfig) => {
                const name = fieldConfig.field_identifier;
                return (
                  <ReviewQuestion
                    doableConfig={doable.configuration}
                    fieldConfig={fieldConfig}
                    responses={responses}
                    key={name}
                    responseToQuestion={responses[name]}
                    showPrevious={showPrevious}
                  />
                );
              })}
            </tbody>
          </ReviewTable>
          <Action small onClick={submitResponses}>
            Submit your answers
          </Action>
        </Card>
      </>
    );
  }

  return (
    <>
      <ScrollToTop />
      <Card title={requestTitle} removeTopPadding>
        {requestDescription && <QuestionDescription dangerouslySetInnerHTML={createContent(requestDescription)} />}
        <React.Fragment>
          {doable.configuration.fields.map((fieldConfig) => {
            const name = fieldConfig.field_identifier;
            return (
              <div key={name} ref={(el) => (fieldRefs.current[name] = el)}>
                <RenderQuestion
                  doableConfig={doable.configuration}
                  fieldConfig={fieldConfig}
                  responses={responses}
                  selected={responses[name]}
                  value={responses[name] || ""}
                  onChange={handleCheckboxChange(name)}
                  onDateChange={handleDateChange(name)}
                  optionSelected={handleInputChange(name)}
                  validate={handleFieldValidation(name)(fieldConfig.field_type)}
                  validateDate={handleDateFieldValidation(name)}
                  hasValidationError={!!errors[name]}
                  validationErrorMessage={errorMessages[name]}
                />
              </div>
            );
          })}

          {checkBeforeSubmit ? (
            <Action small onClick={reviewResponses}>
              Continue
            </Action>
          ) : (
            <Action small onClick={saveResponses}>
              Save your answers
            </Action>
          )}
        </React.Fragment>
      </Card>
    </>
  );
};

AnonymousRequestForm.propTypes = {
  doable: PropTypes.shape({
    id: PropTypes.string.isRequired,
    doable_type: PropTypes.string.isRequired,
    status: PropTypes.string.isRequired,
    title: PropTypes.string.isRequired,
    description: PropTypes.string,
    configuration: PropTypes.shape({
      fields: PropTypes.arrayOf(PropTypes.object).isRequired,
      documents: PropTypes.array,
      check_before_submit: PropTypes.bool,
    }).isRequired,
  }).isRequired,
  pageWidth: PropTypes.oneOf(["narrow", "regular", "wide", "wider"]),
};

export default AnonymousRequestForm;
